import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { apiClient } from '../lib/api-client';

// Navigation icons
const icons = {
  dashboard: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
      />
    </svg>
  ),
  users: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
      />
    </svg>
  ),
  posts: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
      />
    </svg>
  ),
  tickets: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z'
      />
    </svg>
  ),
  tiers: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
      />
    </svg>
  ),
  plans: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
      />
    </svg>
  ),
  notifications: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
      />
    </svg>
  ),
  banners: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z'
      />
    </svg>
  ),
  ogp: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
      />
    </svg>
  ),
  scheduler: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
      />
    </svg>
  ),
  settings: (
    <svg className='h-5 w-5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
      />
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
      />
    </svg>
  )
};

export default function Layout() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('adminSidebarCollapsed') === 'true';
  });
  const [isHovered, setIsHovered] = useState(false);
  const [user, setUser] = useState<{
    id: string;
    username: string;
    displayName: string | null;
  } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // サイドバーが視覚的に展開されているか（固定展開 or ホバーで一時展開）
  const sidebarExpanded = !isCollapsed || isHovered;

  const toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    localStorage.setItem('adminSidebarCollapsed', String(next));
    if (next) {
      setIsHovered(false);
      setUserMenuOpen(false);
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await apiClient.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Failed to load user:', error);
      }
    };
    void loadUser();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    void navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: icons.dashboard },
    { path: '/users', label: 'Users', icon: icons.users },
    { path: '/reserved-actions', label: 'Reserved Actions', icon: icons.posts },
    { path: '/tickets', label: 'Tickets', icon: icons.tickets },
    { path: '/tiers', label: 'Tiers', icon: icons.tiers },
    { path: '/plans', label: 'Plans', icon: icons.plans },
    { path: '/notifications', label: 'Notifications', icon: icons.notifications },
    { path: '/banners', label: 'Banners', icon: icons.banners },
    { path: '/ogp', label: 'OGP', icon: icons.ogp },
    { path: '/scheduler-sessions', label: 'Scheduler Sessions', icon: icons.scheduler },
    { path: '/settings', label: 'Service Settings', icon: icons.settings }
  ];

  return (
    <div className='flex min-h-screen bg-gray-100 dark:bg-gray-900'>
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div className='fixed inset-0 z-40 bg-black/50 md:hidden' onClick={closeMobileMenu} />
      )}

      {/* Sidebar */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white shadow-md dark:bg-gray-800',
          'transition-[width] duration-200 ease-in-out',
          sidebarExpanded ? 'w-64' : 'w-14',
          // モバイル: hamburger で表示切り替え / デスクトップ: 常に表示
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        ].join(' ')}
        onMouseEnter={() => {
          if (isCollapsed) setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          setUserMenuOpen(false);
        }}
      >
        {/* ヘッダー */}
        <div
          className={[
            'flex h-14 flex-shrink-0 items-center border-b border-gray-200 dark:border-gray-700',
            sidebarExpanded ? 'px-4' : 'justify-center px-2'
          ].join(' ')}
        >
          {sidebarExpanded ? (
            <h1 className='truncate text-lg font-bold text-gray-800 dark:text-white'>
              Chronosky Admin
            </h1>
          ) : (
            <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white'>
              C
            </div>
          )}
        </div>

        {/* ナビゲーション */}
        <nav className='flex-1 overflow-y-auto py-2'>
          {navItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                title={!sidebarExpanded ? item.label : undefined}
                className={[
                  'flex items-center gap-3 transition-colors',
                  sidebarExpanded ? 'px-4 py-2.5' : 'justify-center px-2 py-2.5',
                  isActive
                    ? 'bg-gray-200 font-semibold text-gray-900 dark:bg-gray-700 dark:text-white'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                ].join(' ')}
              >
                {item.icon}
                {sidebarExpanded && <span className='truncate text-sm'>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* ユーザーメニュー */}
        <div className='flex-shrink-0 border-t border-gray-200 dark:border-gray-700'>
          <div
            className={['p-3', !sidebarExpanded && 'flex justify-center'].filter(Boolean).join(' ')}
            ref={userMenuRef}
          >
            {sidebarExpanded ? (
              <div className='relative'>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className='flex w-full items-center gap-3 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-700'
                >
                  <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900'>
                    <span className='text-sm font-medium text-blue-600 dark:text-blue-300'>
                      {(user?.displayName ?? user?.username ?? 'A').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className='min-w-0 flex-1 text-left'>
                    <div className='truncate text-sm font-medium text-gray-900 dark:text-white'>
                      {user?.displayName ?? user?.username ?? 'Admin'}
                    </div>
                    <div className='truncate text-xs text-gray-500 dark:text-gray-400'>
                      @{user?.username ?? 'admin'}
                    </div>
                  </div>
                  <svg
                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M19 9l-7 7-7-7'
                    />
                  </svg>
                </button>

                {userMenuOpen && (
                  <div className='absolute right-0 bottom-full left-0 mb-2 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800'>
                    <div className='p-2'>
                      <Link
                        to='/passkeys'
                        onClick={() => setUserMenuOpen(false)}
                        className='flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white'
                      >
                        <svg
                          className='mr-2 h-4 w-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                          />
                        </svg>
                        Passkeys
                      </Link>

                      <button
                        onClick={() => {
                          toggleTheme();
                          setUserMenuOpen(false);
                        }}
                        className='flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white'
                      >
                        {theme === 'dark' ? (
                          <>
                            <svg
                              className='mr-2 h-4 w-4'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z'
                              />
                            </svg>
                            Light Mode
                          </>
                        ) : (
                          <>
                            <svg
                              className='mr-2 h-4 w-4'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z'
                              />
                            </svg>
                            Dark Mode
                          </>
                        )}
                      </button>

                      <div className='my-2 border-t border-gray-200 dark:border-gray-700' />

                      <button
                        onClick={() => {
                          handleLogout();
                          setUserMenuOpen(false);
                        }}
                        className='flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                      >
                        <svg
                          className='mr-2 h-4 w-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                          />
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                className='flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900'
                title={user?.displayName ?? user?.username ?? 'Admin'}
              >
                <span className='text-sm font-medium text-blue-600 dark:text-blue-300'>
                  {(user?.displayName ?? user?.username ?? 'A').charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* 折りたたみトグルボタン（デスクトップのみ） */}
          <div
            className={[
              'hidden border-t border-gray-200 p-2 md:flex dark:border-gray-700',
              sidebarExpanded ? 'justify-end' : 'justify-center'
            ].join(' ')}
          >
            <button
              onClick={toggleCollapse}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className='rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
            >
              {isCollapsed ? (
                // 展開アイコン (>>)
                <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M13 5l7 7-7 7M5 5l7 7-7 7'
                  />
                </svg>
              ) : (
                // 縮小アイコン (<<)
                <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M11 19l-7-7 7-7m8 14l-7-7 7-7'
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* メインコンテンツ - サイドバー幅に応じてマージン調整 */}
      <main
        className={[
          'min-w-0 flex-1 text-gray-900 transition-[margin] duration-200 ease-in-out dark:text-gray-100',
          isCollapsed ? 'md:ml-14' : 'md:ml-64'
        ].join(' ')}
      >
        {/* モバイルヘッダー */}
        <div className='sticky top-0 z-30 flex items-center gap-4 border-b border-gray-200 bg-white p-4 md:hidden dark:border-gray-700 dark:bg-gray-800'>
          <button
            onClick={toggleMobileMenu}
            className='rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
            aria-label='Toggle menu'
          >
            <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M4 6h16M4 12h16M4 18h16'
              />
            </svg>
          </button>
          <h2 className='text-lg font-semibold text-gray-800 dark:text-white'>Chronosky Admin</h2>
        </div>

        {/* ページコンテンツ */}
        <div className='p-4 md:p-6 lg:p-8'>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
